<template>
  <div id="app-root">
    <template v-if="isPublicRoute">
      <router-view />
    </template>
    <template v-else>
      <div class="layout">
        <AppSidebar @logout="handleLogout" />
        <main class="main-content">
          <router-view />
        </main>
      </div>
    </template>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppSidebar from '@/components/AppSidebar.vue'
import { useAuth } from '@/composables/useAuth'
import api from '@/api'

export default {
  name: 'App',
  components: { AppSidebar },
  setup() {
    const route  = useRoute()
    const router = useRouter()
    const { clearSession } = useAuth()

    const isPublicRoute = computed(() => route.meta.public)

    async function handleLogout() {
      try { await api.logout() } catch {}
      clearSession()
      router.push({ name: 'login' })
    }

    return { isPublicRoute, handleLogout }
  },
}
</script>

<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --bg:       #0f172a;
  --surface:  #1e293b;
  --border:   #334155;
  --text:     #e2e8f0;
  --muted:    #94a3b8;
  --primary:  #6366f1;
  --primary-h:#818cf8;
  --success:  #22c55e;
  --warning:  #f59e0b;
  --danger:   #ef4444;
  --low:      #64748b;
  --normal:   #6366f1;
  --high:     #f59e0b;
  --critical: #ef4444;
  --radius:   8px;
  --shadow:   0 4px 16px rgba(0,0,0,.4);
}

body {
  background: var(--bg);
  color: var(--text);
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  min-height: 100vh;
}

.layout {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 28px 32px;
  min-width: 0;
}

/* shared utility classes */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: var(--radius);
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: opacity .15s, background .15s;
}
.btn:hover { opacity: .85; }
.btn:disabled { opacity: .4; cursor: not-allowed; }
.btn-primary   { background: var(--primary); color: #fff; }
.btn-success   { background: var(--success); color: #fff; }
.btn-danger    { background: var(--danger);  color: #fff; }
.btn-ghost     { background: transparent; color: var(--muted); border: 1px solid var(--border); }
.btn-ghost:hover { color: var(--text); border-color: var(--primary); }
.btn-sm        { padding: 4px 10px; font-size: 12px; }
.btn-icon      { padding: 6px; border-radius: 6px; }

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .4px;
}
.badge-low      { background: #1e293b; color: var(--low);      border: 1px solid var(--low); }
.badge-normal   { background: #1e293b; color: var(--normal);   border: 1px solid var(--normal); }
.badge-high     { background: #451a03; color: var(--high);     border: 1px solid var(--high); }
.badge-critical { background: #450a0a; color: var(--critical); border: 1px solid var(--critical); }
.badge-open        { background: #1e293b; color: var(--muted);   border: 1px solid var(--border); }
.badge-in_progress { background: #1e1b4b; color: var(--primary); border: 1px solid var(--primary); }
.badge-done        { background: #052e16; color: var(--success); border: 1px solid var(--success); }

.form-group { margin-bottom: 16px; }
.form-group label { display: block; font-size: 12px; color: var(--muted); margin-bottom: 6px; font-weight: 500; }
.form-control {
  width: 100%; padding: 9px 12px;
  background: var(--bg); border: 1px solid var(--border);
  border-radius: var(--radius); color: var(--text);
  font-size: 13px; outline: none;
  transition: border-color .15s;
}
.form-control:focus { border-color: var(--primary); }
.form-control::placeholder { color: var(--muted); }

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 20px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}
.page-title { font-size: 20px; font-weight: 700; }

.spinner {
  width: 32px; height: 32px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin .7s linear infinite;
  margin: 60px auto;
}
@keyframes spin { to { transform: rotate(360deg); } }

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--muted);
}
.empty-state p { margin-top: 8px; font-size: 13px; }

a { color: var(--primary); text-decoration: none; }
a:hover { color: var(--primary-h); }

select.form-control option { background: var(--surface); }
</style>
